import axios from "axios";
import { isReplayable, enqueue } from "@/lib/offline";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  // Évite les chargements infinis si le serveur ne répond pas (prod lente / coupure)
  timeout: 30000,
});

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("mazid_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("mazid_token");
      localStorage.removeItem("mazid_user");
      // Session expirée dans le portail → retour à la connexion (jamais pour les appels /auth/)
      const reqUrl = err.config?.url || "";
      if (window.location.pathname.startsWith("/dashboard") && !reqUrl.includes("/auth/")) {
        window.location.href = "/connexion?session=expiree";
      }
    }
    // MODE HORS-LIGNE : les écritures sûres sont mises en file et rejouées au retour du réseau
    if (!err.response && err.config && !err.config.__noQueue) {
      const path = (err.config.url || "").replace(err.config.baseURL || "", "");
      if (isReplayable(err.config.method, path)) {
        let body = err.config.data;
        try { body = typeof body === "string" ? JSON.parse(body) : body; } catch { /* garde brut */ }
        enqueue(err.config.method, path.startsWith("/") ? path : `/${path}`, body);
        // Réponse synthétique 202 : l'utilisateur continue à travailler normalement
        return Promise.resolve({
          status: 202, statusText: "Queued Offline",
          data: { queued: true, offline: true, message: "Enregistré hors-ligne — sera synchronisé automatiquement." },
          headers: {}, config: err.config,
        });
      }
    }
    return Promise.reject(err);
  }
);

// Ouvre un fichier protégé (PDF/image) en incluant le jeton d'authentification.
// window.open direct sur l'URL backend échoue avec 401 "Token requis".
export const openAuthedFile = async (path) => {
  const { data, headers } = await api.get(path, { responseType: "blob", timeout: 120000 });
  const blob = new Blob([data], { type: headers["content-type"] || "application/pdf" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// Télécharge un fichier protégé avec le jeton, en forçant l'enregistrement (CSV, TXT...)
export const downloadAuthedFile = async (path, filename) => {
  const { data, headers } = await api.get(path, { responseType: "blob", timeout: 120000 });
  const blob = new Blob([data], { type: headers["content-type"] || "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export const fmtGNF = (amount) => {
  if (amount === null || amount === undefined) return "0 GNF";
  return new Intl.NumberFormat("fr-FR").format(Math.round(amount)) + " GNF";
};

export const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { year: "numeric", month: "short", day: "numeric" });
};
